import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "Components/Base/BaseLoader";
import { toast } from "react-toastify";
import {
  checkStatusCodeSuccess,
  notFound,
  PaymentMode,
  TaxType,
} from "Components/constants/Common";
import { viewOrderApi } from "Api/Order";
import TableContainer from "Components/Base/BaseTable";
import { RiSearchLine } from "react-icons/ri";
import moment from "moment";
import {
  FINAL_AMOUNT_LABEL,
  ORDER_STATUS_KEY,
  ORDER_STATUS_LABEL,
  ORDER_TOTAL_LABEL,
  ORDER_TYPE,
  OUTSTANDING_AMOUNT,
  OUTSTANDING_AMOUNT_KEY,
  PARTIALLY_REFUNDED_LABEL,
  PAYABLE_AMOUNT,
  PAYABLE_AMOUNT_KEY,
  PAYMENT_DETAILS_LABEL,
  PAYMENT_MODE,
  PAYMENT_MODE_KEY,
  PRODUCT_CODE_KEY,
  RATE_KEY,
  RECEIVED_DATE,
  REFUND_DETAILS_LABEL,
  REMARKS_KEY,
  REMARKS_LABEL,
  SHIPMENT_DATE_LABEL,
  TAX_KEY,
  TAX_LABEL,
  VIEW_ORDER_LABEL,
} from "Components/constants/Order";
import {
  AmountLabel,
  GRN_STATUS_LABEL,
  OrderDateLabel,
  PO_ID_LABEL,
  ProductCodeLabel,
  PurchaseOrderOptions,
  QuantityKey,
  QuantityLabel,
  RateLabel,
  ReceivedDateKey,
  ReceivedDateLabel,
  VendorNameLabel,
} from "Components/constants/PurchaseOrder";
import { CustomerNameLabel } from "Components/constants/Staff";
import { projectName } from "Components/constants/Project";
import { ActionKey, ActionLabel, StatusLabel } from "Components/constants/Role";
import { viewPurchaseOrder } from "Api/PurchaseOrder";
import { DELIVERY_DATE } from "Components/constants/Reports";

interface OrderHistory {
  id: number;
  order_id: number;
  total_amount: number;
  received_amount: number;
  out_standing_amount: number;
  payable_amount: number;
  received_date: string;
  payment_mode: string;
  remarks: string;
}

interface OrderItem {
  quantity: number;
  rate: number;
  tax: string;
  final_amount: number;
  order_status: string;
  Product: {
    id: number;
    product_code: string;
  };
}

interface GRNHistoryItem {
  id: number;
  date_of_issue: string;
  product_id: string;
  received_quantity: number;
  pending_quantity: number;
  version: string;
}

interface History {
  id: number;
  order_type: string;
  order_date: string;
  expected_shipment_date: string;
  discount_value: number;
  total_price: number;
  payment_status: string;
  po_status: string;
  authUser: {
    id: number;
    name: string;
  };
  project: {
    id: number;
    project_name: string;
  };
  orderItem?: OrderItem[];
  purchaseOrderHistory?: OrderHistory[];
  grnHistory?: GRNHistoryItem[];
}

const ViewPurchaseOrder = () => {
  document.title = VIEW_ORDER_LABEL;
  const navigate = useNavigate();
  const { id } = useParams();
  const [loader, setLoader] = useState<boolean>(false);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [customPageSize, setCustomPageSize] = useState<number>(60);
  const [history, setHistory] = useState<History>();
  const [versions, setVersions] = useState<string[]>([]);

  const fetchProduct = () => {
    setLoader(true);
    viewPurchaseOrder(Number(id))
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setHistory(resp?.data);
          // Extract unique versions with proper typing
          const versions = Array.from(
            new Set<string>(
              resp?.data?.grnHistory?.map(
                (item: GRNHistoryItem) => item.version
              ) || []
            )
          );
          setVersions(versions);
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const orderColumns = useMemo(
    () => [
      {
        header: PAYABLE_AMOUNT,
        accessorKey: PAYABLE_AMOUNT_KEY,
        cell: (cell: any) => {
          return (
            <span>
              <i className="mdi mdi-currency-rupee"></i>
              {cell?.row?.original?.payable_amount}
            </span>
          );
        },
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Received Amount",
        accessorKey: "received_amount",
        cell: (cell: any) => {
          return (
            <span>
              <i className="mdi mdi-currency-rupee"></i>
              {cell?.row?.original?.received_amount}
            </span>
          );
        },
        enableColumnFilter: false,
      },
      {
        header: OUTSTANDING_AMOUNT,
        accessorKey: OUTSTANDING_AMOUNT_KEY,
        cell: (cell: any) => {
          return (
            <span>
              <i className="mdi mdi-currency-rupee"></i>
              {cell?.row?.original?.out_standing_amount}
            </span>
          );
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: ReceivedDateLabel,
        accessorKey: ReceivedDateKey,
        cell: (cell: any) => {
          return (
            <div>
              <span>
                {moment(cell?.row?.original?.received_date).format("ll")}
              </span>
            </div>
          );
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: PAYMENT_MODE,
        accessorKey: PAYMENT_MODE_KEY,
        cell: (cell: any) => {
          return (
            <div>
              <span className="badge bg-dark">
                {cell?.row?.original?.payment_mode}
              </span>
            </div>
          );
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: REMARKS_LABEL,
        accessorKey: REMARKS_KEY,
        enableSorting: true,
        enableColumnFilter: false,
      },
    ],
    []
  );
  const purchaseOrderGRNHistory = useMemo(
    () => [
      {
        header: "Date",
        accessorKey: "date_of_issue",
        cell: (cell: any) => {
          return moment(cell?.row?.original.date_of_issue).format("ll");
        },
        enableColumnFilter: false,
      },
      {
        header: ProductCodeLabel,
        accessorKey: "product.product_code",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Received Quantity",
        accessorKey: "received_quantity",
        enableColumnFilter: false,
      },
      {
        header: "Pending Quantity",
        accessorKey: "pending_quantity",
        enableColumnFilter: false,
      },
    ],
    []
  );

  const statusColorMap: { [key: string]: string } = {
    Pending: "bg-warning",
    "Partially Paid": "bg-info",
    Paid: "bg-success",
    "Partially Refunded": "bg-primary",
    Refunded: "bg-danger",
  };

  useEffect(() => {
    fetchProduct();
  }, []);

  return (
    <div className="page-content">
      {loader && <Loader />}
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4">View Purchase Order History</span>
          <button
            className="btn btn-danger"
            onClick={() => navigate("/purchase-order")}
          >
            <i className="mdi mdi-arrow-left" />
          </button>
        </div>

        {/* Order Details Card */}
        <div className="card">
          <div className="row">
            <div className="col-lg-12">
              <div className="card-body py-2 px-4">
                <div className="row g-3">
                  <div className="col-lg-2 col-md-4 col-6">
                    <p className="text-muted mb-2 text-uppercase fw-semibold">
                      {VendorNameLabel}
                    </p>
                    <h5 className="fs-14 mb-0">{history?.authUser?.name}</h5>
                  </div>

                  <div className="col-lg-2 col-md-4 col-6">
                    <p className="text-muted mb-2 text-uppercase fw-semibold">
                      {PO_ID_LABEL}
                    </p>
                    <h5 className="fs-14 mb-0">PO{history?.id}</h5>
                  </div>

                  <div className="col-lg-2 col-md-4 col-6">
                    <p className="text-muted mb-2 text-uppercase fw-semibold">
                      {GRN_STATUS_LABEL}
                    </p>
                    <span
                      className={
                        history?.po_status === PurchaseOrderOptions.RECEIVED
                          ? "badge bg-success"
                          : history?.po_status === PurchaseOrderOptions.PARTIALLY_RECEIVED
                          ? "badge bg-primary"
                          : "badge bg-info"
                      }
                    >
                      {history?.po_status || "-"}
                    </span>
                  </div>

                  <div className="col-lg-2 col-md-4 col-6">
                    <p className="text-muted mb-2 text-uppercase fw-semibold">
                      Payment Status
                    </p>
                    <span
                      className={`badge ${
                        statusColorMap[history?.payment_status || "Pending"]
                      }`}
                    >
                      {history?.payment_status || "Pending"}
                    </span>
                  </div>

                  <div className="col-lg-2 col-md-4 col-6">
                    <p className="text-muted mb-2 text-uppercase fw-semibold">
                      {OrderDateLabel}
                    </p>
                    <h5 className="fs-14 mb-0">
                      {moment(history?.order_date).format("ll")}
                    </h5>
                  </div>

                  <div className="col-lg-2 col-md-4 col-6">
                    <p className="text-muted mb-2 text-uppercase fw-semibold">
                      {DELIVERY_DATE}
                    </p>
                    <h5 className="fs-14 mb-0">
                      {moment(history?.expected_shipment_date).format("ll")}
                    </h5>
                  </div>
                </div>
              </div>
            </div>

            {history?.purchaseOrderHistory &&
              history?.purchaseOrderHistory?.length > 0 && (
                <div className="col-lg-12">
                  <div className="card-body py-2 px-4">
                    <div className="border-top border-top-dashed mt-2"></div>
                    <div className="mt-3">
                      <h6 className="text-muted text-uppercase fw-semibold mb-3">
                        {PAYMENT_DETAILS_LABEL}
                      </h6>
                      <div className="table-responsive mt-2">
                        {loader ||
                        (history?.purchaseOrderHistory &&
                          history?.purchaseOrderHistory?.length > 0) ? (
                          <TableContainer
                            customPageSize={customPageSize}
                            setCustomPageSize={setCustomPageSize}
                            isGlobalFilter={false}
                            data={history?.purchaseOrderHistory || []}
                            columns={orderColumns}
                            tableClass="table bg-white"
                            thClass="bg-light"
                            manualPagination={true}
                            manualFiltering={false}
                            tableLoader={loader}
                            hasManualPagination={false}
                            currentPage={1}
                            setCurrentPage={setCurrentPage}
                            totalPages={totalPages}
                            totalRecords={totalRecords}
                            totalNumberOfRows={totalNumberOfRows}
                            fetchData={() => {}}
                            fetchSortingData={() => {}}
                          />
                        ) : (
                          <div className="py-4 text-center">
                            <div>
                              <span className="fs-1 text-success">
                                <RiSearchLine />
                              </span>
                            </div>
                            <div className="mt-4">
                              <h5>{notFound.dataNotFound}</h5>
                              <p className="text-muted">
                                {notFound.thereIsNoDataFound}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {/* Version Tables */}
            {versions.map((version) => (
              <div className="col-lg-12" key={version}>
                <div className="card-body py-2 px-4">
                  <div className="border-top border-top-dashed mt-2"></div>
                  <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="text-muted text-uppercase fw-semibold">
                        GRN History {version}
                      </h6>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() =>
                          navigate(`/compose-grn-invoice/${id}/${version}`)
                        }
                      >
                        <i className="mdi mdi-mail me-1"></i>
                        Email
                      </button>
                    </div>
                    <div className="table-responsive mt-2">
                      {loader ||
                      (history?.grnHistory &&
                        history?.grnHistory?.length > 0) ? (
                        <TableContainer
                          customPageSize={customPageSize}
                          setCustomPageSize={setCustomPageSize}
                          isGlobalFilter={false}
                          data={
                            history?.grnHistory?.filter(
                              (item) => item.version === version
                            ) || []
                          }
                          columns={purchaseOrderGRNHistory}
                          tableClass="table bg-white"
                          thClass="bg-light"
                          manualPagination={true}
                          manualFiltering={false}
                          tableLoader={loader}
                          hasManualPagination={false}
                          currentPage={1}
                          setCurrentPage={setCurrentPage}
                          totalPages={totalPages}
                          totalRecords={totalRecords}
                          totalNumberOfRows={totalNumberOfRows}
                          fetchData={() => {}}
                          fetchSortingData={() => {}}
                        />
                      ) : (
                        <div className="py-4 text-center">
                          <div>
                            <span className="fs-1 text-success">
                              <RiSearchLine />
                            </span>
                          </div>
                          <div className="mt-4">
                            <h5>{notFound.dataNotFound}</h5>
                            <p className="text-muted">
                              {notFound.thereIsNoDataFound}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewPurchaseOrder;
